import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Rating,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  FavoriteBorder,
  Home,
  Public,
  CalendarToday,
  Games as GamesIcon,
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store';
import { fetchGameById, clearCurrentGame } from '../../store/gamesSlice';
import { addToFavorites, removeFromFavorites } from '../../store/favoritesSlice';

const GamePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { currentGame, status, error } = useSelector((state: RootState) => state.games);
  const favorites = useSelector((state: RootState) => state.favorites.items);

  useEffect(() => {
    if (id) {
      dispatch(fetchGameById(parseInt(id)));
    }
    return () => {
      dispatch(clearCurrentGame());
    };
  }, [id, dispatch]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleFavoriteToggle = () => {
    if (currentGame) {
      const isFavorite = favorites.some(fav => fav.id === currentGame.id);
      if (isFavorite) {
        dispatch(removeFromFavorites(currentGame.id));
      } else {
        dispatch(addToFavorites(currentGame));
      }
    }
  };

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  if (status === 'failed') {
    return <Alert severity="error">Ошибка загрузки игры: {error}</Alert>;
  }

  if (!currentGame) {
    return <Alert severity="warning">Игра не найдена</Alert>;
  }

  const isFavorite = favorites.some(fav => fav.id === currentGame.id);

  return (
    <div>
      <Button
        startIcon={<ArrowBack />}
        onClick={handleGoBack}
        style={{ marginBottom: '1rem' }}
      >
        Назад
      </Button>

      <Paper elevation={3} style={{ padding: '2rem' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <img
              src={currentGame.thumbnail}
              alt={currentGame.title}
              style={{ width: '100%', borderRadius: '8px' }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h1" gutterBottom>
              {currentGame.title}
            </Typography>

            <div style={{ marginBottom: '1rem' }}>
              <Rating value={currentGame.rating || 4} readOnly size="large" />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <Chip
                icon={<GamesIcon />}
                label={currentGame.genre}
                style={{ marginRight: '0.5rem', marginBottom: '0.5rem' }}
              />
              <Chip
                icon={<Public />}
                label={currentGame.platform}
                style={{ marginRight: '0.5rem', marginBottom: '0.5rem' }}
              />
              <Chip
                icon={<CalendarToday />}
                label={new Date(currentGame.release_date).toLocaleDateString()}
                style={{ marginBottom: '0.5rem' }}
              />
            </div>

            <Typography variant="body1" paragraph>
              {currentGame.short_description}
            </Typography>

            <Divider style={{ margin: '1rem 0' }} />

            <Typography variant="subtitle1" gutterBottom>
              <strong>Издатель:</strong> {currentGame.publisher}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Разработчик:</strong> {currentGame.developer}
            </Typography>

            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={isFavorite ? <Favorite /> : <FavoriteBorder />}
                onClick={handleFavoriteToggle}
              >
                {isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<Home />}
                component={Link}
                to="/games"
              >
                Все игры
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default GamePage;